import { useCallback, useRef, useSyncExternalStore } from "react";
import { useEqualSelector } from "@/hooks/use-equal-selector";
import type { FrameData } from "@/lib/frame-data";
import { subscribeFrameRects } from "@/lib/frame-screen-rect";
import { isFocusVisibleHere } from "@/lib/focus/use-focus-visible-here";
import type { VisibleConversation } from "@/lib/run-surface-owner";
import { workspaceViewAdapter } from "@/lib/workspace-view-adapter";
import { useCanvasStore } from "@/stores/canvas-store";
import { usePresentationStore } from "@/stores/presentation-store";
import type { PresentationState } from "@/lib/focus/focus-types";
import { frameNodeHidden } from "@/stores/canvas-frame-geometry";
import { useFramesStore } from "@/stores/frames-store";
import {
  isFrameHiddenByMetadata,
  selectFramesByType,
} from "@/stores/frames-selectors";
import { useWorkspaceConversationOverlay } from "@/stores/workspace-conversation-overlay-store";
import { useCompanionStore } from "@/stores/companion-store";
import { useDesktopMode } from "@/stores/desktop-mode-store";
import { useProjectStore } from "@/stores/project-store";
import { resolveCompactFrameOverlayIds } from "./use-compact-frame-overlay";
import { useFloatingFrameStore } from "./floating-frame-store";
import { useActiveDisplay } from "./use-active-display";
import { getWindowDisplayId } from "./use-window-role";

type VisibleComputerConversations = readonly VisibleConversation[];

function text(
  metadata: Record<string, unknown>,
  ...keys: string[]
): string | undefined {
  for (const key of keys) {
    const value = metadata[key];
    if (typeof value === "string" && value.trim()) return value;
  }
}

function visibleConversations(
  presentation: PresentationState | null,
  conversationFrames: readonly FrameData[],
  overlay: ReturnType<
    typeof useWorkspaceConversationOverlay.getState
  >["overlay"],
  frames: ReturnType<typeof useFloatingFrameStore.getState>["frames"],
  desktop: Pick<
    ReturnType<typeof useDesktopMode.getState>,
    "mode" | "compactFrameOverlayIds"
  >,
  projectId: string | undefined,
  spaceId: string | undefined,
  sessions: ReturnType<typeof useCompanionStore.getState>["agentSessions"],
  activeDisplayId: number | null,
): VisibleComputerConversations {
  const metadata: Array<{ id: string; value: Record<string, unknown> }> = [];
  const add = (id: string, value: Record<string, unknown>) =>
    metadata.push({ id, value });
  const peekActive =
    desktop.mode !== "peek" ||
    getWindowDisplayId() === null ||
    activeDisplayId === null ||
    getWindowDisplayId() === activeDisplayId;
  // A focused chat owns its run wherever this window shows the focus,
  // over the wall or on the canvas.
  if (presentation?.variant === "focus" && peekActive && isFocusVisibleHere()) {
    const ids = new Set(
      desktop.mode !== "compact"
        ? presentation.frameIds
        : resolveCompactFrameOverlayIds({
            mode: desktop.mode,
            requestedFrameIds: desktop.compactFrameOverlayIds,
            presentation,
          }),
    );
    const { focusHiddenFrameIds } = useCanvasStore.getState();
    for (const frame of conversationFrames) {
      if (
        ids.has(frame.frameId) &&
        !frameNodeHidden(frame, focusHiddenFrameIds)
      )
        add(frame.frameId, frame.metadata);
    }
  } else if (
    presentation?.variant !== "focus" &&
    desktop.mode !== "compact" &&
    spaceId &&
    peekActive
  ) {
    // Chats the workspace view itself shows (canvas nodes; never wall
    // cards, so the wall answers at once while it scrolls).
    const shown = new Set(
      workspaceViewAdapter().visibleConversationIds(
        conversationFrames.map((frame) => frame.frameId),
      ),
    );
    for (const frame of conversationFrames) {
      if (
        shown.has(frame.frameId) &&
        !isFrameHiddenByMetadata(frame) &&
        frame.spaceId === spaceId &&
        !frames[frame.frameId]
      )
        add(frame.frameId, frame.metadata);
    }
  }
  if (
    overlay &&
    overlay.project.id === projectId &&
    overlay.space.id === spaceId
  )
    add(overlay.frame.id, overlay.frame.metadata);
  const conversationsById = new Map(
    conversationFrames.map((frame) => [frame.frameId, frame]),
  );
  for (const floating of Object.values(frames)) {
    const frame = conversationsById.get(floating.frameId) ?? floating.frame;
    if (
      floating.projectId === projectId &&
      floating.spaceId === spaceId &&
      frame.frameType === "conversation" &&
      !isFrameHiddenByMetadata(frame)
    )
      add(floating.frameId, frame.metadata);
  }
  return metadata.map(({ id, value }) => {
    const session = Object.values(sessions).find(
      (current) => current.conversationFrameId === id,
    );
    return {
      frameId: id,
      conversationId:
        text(value, "conversationId", "conversation_id") ??
        session?.conversationId,
      executionId:
        text(value, "executionId", "execution_id", "liveExecutionId") ??
        session?.executionId,
      runtimeSessionId:
        text(
          value,
          "runtimeSessionId",
          "runtime_session_id",
          "liveSessionId",
          "sessionId",
        ) ?? session?.runtimeSessionId,
    };
  });
}

export function getVisibleComputerConversations(): VisibleComputerConversations {
  return visibleConversations(
    usePresentationStore.getState().presentation,
    selectFramesByType(useFramesStore.getState(), "conversation"),
    useWorkspaceConversationOverlay.getState().overlay,
    useFloatingFrameStore.getState().frames,
    useDesktopMode.getState(),
    useProjectStore.getState().currentProject?.id,
    useProjectStore.getState().currentSpace?.id,
    useCompanionStore.getState().agentSessions,
    useActiveDisplay.getState().activeDisplayId,
  );
}

/** Content streaming into a chat leaves the fields read here unchanged. */
function sameConversationFrames(
  left: readonly FrameData[],
  right: readonly FrameData[],
): boolean {
  return (
    left.length === right.length &&
    left.every((frame, index) => {
      const other = right[index]!;
      return (
        frame === other ||
        (frame.frameId === other.frameId &&
          frame.spaceId === other.spaceId &&
          frame.metadata === other.metadata)
      );
    })
  );
}

function sameConversations(
  left: VisibleComputerConversations,
  right: VisibleComputerConversations,
): boolean {
  return (
    left.length === right.length &&
    left.every((conversation, index) => {
      const other = right[index]!;
      return (
        conversation.frameId === other.frameId &&
        conversation.conversationId === other.conversationId &&
        conversation.executionId === other.executionId &&
        conversation.runtimeSessionId === other.runtimeSessionId
      );
    })
  );
}

export function useVisibleComputerConversations(): VisibleComputerConversations {
  const conversationFrames = useFramesStore(
    useEqualSelector(
      (state) => selectFramesByType(state, "conversation"),
      sameConversationFrames,
    ),
  );
  const overlay = useWorkspaceConversationOverlay((state) => state.overlay);
  const frames = useFloatingFrameStore((state) => state.frames);
  const mode = useDesktopMode((state) => state.mode);
  const compactFrameOverlayIds = useDesktopMode(
    (state) => state.compactFrameOverlayIds,
  );
  const projectId = useProjectStore((state) => state.currentProject?.id);
  const spaceId = useProjectStore((state) => state.currentSpace?.id);
  const sessions = useCompanionStore((state) => state.agentSessions);
  const activeDisplayId = useActiveDisplay((state) => state.activeDisplayId);
  const presentation = usePresentationStore((state) => state.presentation);
  const latestRef = useRef<VisibleComputerConversations>([]);
  // Pans, scrolls and window moves re-run the derivation (once per frame)
  // but re-render only when the visible conversation set itself changes.
  const snapshot = useCallback(() => {
    const next = visibleConversations(
      presentation,
      conversationFrames,
      overlay,
      frames,
      { mode, compactFrameOverlayIds },
      projectId,
      spaceId,
      sessions,
      activeDisplayId,
    );
    if (!sameConversations(latestRef.current, next)) latestRef.current = next;
    return latestRef.current;
  }, [
    activeDisplayId,
    compactFrameOverlayIds,
    conversationFrames,
    frames,
    mode,
    overlay,
    presentation,
    projectId,
    sessions,
    spaceId,
  ]);
  return useSyncExternalStore(subscribeFrameRects, snapshot);
}
